import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Zap, Package, Anchor, Building2, Settings, BarChart3, CheckCircle, Download } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { services } from '../mockData';

const serviceIcons = [Zap, Package, Anchor, Building2, Settings, BarChart3];

const whyUs = [
  { title: 'Certified Engineers', desc: 'Siemens, Schneider Electric, Allen-Bradley, and Delta certified professionals on every project.' },
  { title: 'Custom Solutions', desc: 'Every automation system is engineered from the ground up to match your exact requirements.' },
  { title: 'Rigorous Quality', desc: 'Full FAT/SAT testing protocols. Every panel leaves our workshop inspection-ready.' },
  { title: '24/7 Support', desc: 'Round-the-clock on-site and remote assistance to keep your operations running.' },
];

export const ServicesPage = () => {
  return (
    <>
      <SEOHead
        title="Automation Services in UAE & Africa | PLC, SCADA, BMS, Panels"
        description="Automation services in UAE and Africa covering PLC programming, SCADA development, BMS, HVAC automation, control panel manufacturing and switchgear support."
        keywords="automation services UAE, automation services Africa, PLC programming UAE, SCADA development UAE, BMS solutions UAE, HVAC automation UAE, control panel manufacturing UAE, switchgear manufacturing UAE"
      />

      <div className="min-h-screen">

        {/* ═══════════════════ PAGE HERO ═══════════════════ */}
        <section className="relative pt-16 md:pt-[106px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=1920&h=600&fit=crop&q=80"
              alt="Services background"
              width="1920"
              height="600"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E]/90 via-[#0A0F1E]/75 to-[#0A0F1E]/40" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-300">Services</span>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-[2px] bg-[#0057B8]" />
              <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">What We Offer</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4">
              Automation Services in UAE & Africa
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              Comprehensive PLC, SCADA, BMS, HVAC automation, control panel manufacturing, and switchgear support from design to commissioning for UAE and Africa projects.
            </p>
          </div>
        </section>

        {/* ═══════════════════ SERVICES GRID ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service, index) => {
                const Icon = serviceIcons[index] || Zap;
                return (
                  <div key={service.id} className="bg-white group hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#0057B8]/20">
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={service.image}
                        alt={`${service.title} for UAE and Africa automation services`}
                        width="800"
                        height="600"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/80 to-transparent" />
                      {/* Icon badge */}
                      <div className="absolute bottom-4 left-5 w-10 h-10 bg-[#0057B8] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-7">
                      <h3 className="text-xl font-black text-[#0A0F1E] mb-3 group-hover:text-[#0057B8] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-500 text-base leading-relaxed mb-5">
                        {service.description}
                      </p>

                      {service.features && service.features.length > 0 && (
                        <ul className="space-y-2 mb-6">
                          {service.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <div className="flex-shrink-0 w-4 h-4 bg-[#0057B8] flex items-center justify-center mt-0.5">
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <span className="text-gray-600 text-base">{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <Link to={`/services/${service.slug}`} className="inline-flex items-center gap-2 text-[#0057B8] hover:text-[#003D82] font-semibold text-base transition-colors group/link">
                        Learn More
                        <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════ WHY CHOOSE US ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-[2px] bg-[#0057B8]" />
                <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">Our Advantage</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E]">
                Why Choose Our Services?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyUs.map((item, i) => (
                <div key={i} className="border border-gray-200 hover:border-[#0057B8]/50 bg-white hover:shadow-lg p-7 transition-all duration-300 group">
                  <div className="w-10 h-10 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center mb-5 group-hover:bg-[#0057B8] transition-colors">
                    <CheckCircle className="w-5 h-5 text-[#0057B8] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-[#0A0F1E] font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-base leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ CTA ═══════════════════ */}
        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-10">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                Let's Discuss Your UAE or Africa Automation Project
              </h2>
              <p className="text-blue-200 max-w-lg">
                Tell us about your PLC, SCADA, BMS, control panel, or switchgear requirements and our engineers will design the right solution.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link to="/contact">
                <button className="inline-flex items-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 group">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 text-base tracking-wide transition-all duration-200 group"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Our Brochure
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

